/**
 * Created: 2026-10-21
 * Initial commit: Coach calendar page in the web coach app
 *
 * Loads a site's courts, slot templates and one day's slots, and derives what
 * the slot grid renders: the slot lookup, the hours to show, the merged span
 * blocks and the past-hour threshold. Shared by the admin slots page and the
 * coach calendar page.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { computeSpanBlocks } from '@tc-app/shared/slots/mergeSlots'
import { computeDisplayHours } from '@tc-app/shared/slots/slotHours'
import { format, isBefore, isToday } from 'date-fns'
import { useMemo } from 'react'
import { SiteDocument, SlotsDocument, SlotTemplatesDocument } from '../../gql/graphql'
import type { SlotMeta } from './types'

gql`
  query Slots($siteId: UUID!, $date: Date!) {
    slots(siteId: $siteId, date: $date) {
      courtId
      startTime
      price
      status
      reason
      orderId
      eventId
      event {
        gameFormat
        minRating
        maxRating
        drawSize
        numSignups
        fee
      }
      coaching {
        sessionId
        seats
        coach {
          id
          openid
        }
        attendees {
          status
          student {
            name
          }
        }
      }
    }
  }
`

/** Current hour in China timezone (0-23) */
function getChinaHour() {
  return (new Date().getUTCHours() + 8) % 24
}

/** Today's date in China timezone as a Date (midnight local) */
function getChinaToday() {
  const now = new Date()
  const chinaMs = now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60_000
  const china = new Date(chinaMs)
  return new Date(china.getFullYear(), china.getMonth(), china.getDate())
}

export function useSiteSlots(siteId: string | undefined, selectedDate: Date) {
  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(SiteDocument, {
    variables: { siteId: siteId as string },
    skip: !siteId,
    fetchPolicy: 'cache-first',
  })

  const {
    data: slotsData,
    loading: slotsLoading,
    error: slotsError,
    refetch: slotsRefetch,
  } = useQuery(SlotsDocument, {
    variables: {
      siteId: siteId as string,
      date: format(selectedDate, 'yyyy-MM-dd'),
    },
    skip: !siteId,
    fetchPolicy: 'no-cache',
  })

  const { data: templatesData } = useQuery(SlotTemplatesDocument, {
    variables: { siteId: siteId as string },
    skip: !siteId,
    fetchPolicy: 'cache-first',
  })

  const courts = useMemo(() => {
    const courtsList = sitesData?.site.courts ?? []
    return [...courtsList].sort((a, b) => a.displayOrder - b.displayOrder)
  }, [sitesData])

  const { slotLookup, hours } = useMemo(() => {
    const slots = slotsData?.slots ?? []
    const lookup = new Map<string, SlotMeta>()
    for (const s of slots) {
      const chinaHour = (new Date(s.startTime).getUTCHours() + 8) % 24
      lookup.set(`${chinaHour}-${s.courtId}`, {
        price: s.price ?? '',
        status: s.status,
        startTime: s.startTime,
        reason: s.reason ?? undefined,
        orderId: s.orderId ?? undefined,
        eventId: s.eventId ?? undefined,
        event: s.event
          ? {
              gameFormat: s.event.gameFormat,
              minRating: s.event.minRating ?? undefined,
              maxRating: s.event.maxRating ?? undefined,
              drawSize: s.event.drawSize ?? undefined,
              numSignups: s.event.numSignups,
              fee: s.event.fee,
            }
          : undefined,
        sessionId: s.coaching?.sessionId,
        coaching: s.coaching
          ? {
              sessionId: s.coaching.sessionId,
              seats: s.coaching.seats,
              coachId: s.coaching.coach?.id ?? undefined,
              attendees: s.coaching.attendees.map((a) => ({
                status: a.status,
                name: a.student.name ?? undefined,
              })),
            }
          : undefined,
      })
    }
    return {
      slotLookup: lookup,
      hours: computeDisplayHours(
        slots.map((s) => s.startTime),
        templatesData?.slotTemplates ?? [],
        selectedDate,
      ),
    }
  }, [slotsData, templatesData, selectedDate])

  // Compute span blocks (merged cells), covered cells, and span maps for drag selection.
  // Merges both vertically (consecutive hours, same court) and horizontally (adjacent courts)
  // when cells share the same orderId, eventId, sessionId, or reason.
  const { spanBlocks, coveredCells, spanKeyMap, spanGroupCells } = useMemo(
    () => computeSpanBlocks(courts, hours, slotLookup),
    [courts, slotLookup, hours],
  )

  const pastHourThreshold = useMemo(() => {
    const chinaToday = getChinaToday()
    if (isBefore(selectedDate, chinaToday)) return 24
    if (
      isToday(chinaToday) &&
      format(selectedDate, 'yyyy-MM-dd') === format(chinaToday, 'yyyy-MM-dd')
    )
      return getChinaHour()
    return 0
  }, [selectedDate])

  const refetchSlots = () => {
    if (!siteId) return
    return slotsRefetch()
  }

  return {
    courts,
    slotLookup,
    hours,
    spanBlocks,
    coveredCells,
    spanKeyMap,
    spanGroupCells,
    pastHourThreshold,
    refetchSlots,
    loading: sitesLoading || slotsLoading,
    error: sitesError || slotsError,
  }
}
